import { useEffect, useState, type Key, type ReactNode } from "react";
import { Empty, Pagination, Spin } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import { palette } from "../theme";
import { useIsMobile } from "./useIsMobile";
import { ResizableTable } from "./ResizableTable";

/** Configurazione compatta della scheda mobile: codice in monospazio, titolo (max 2 righe), pill, campi etichetta/valore. */
export interface CardSpec<T> {
  code?: (r: T) => ReactNode;
  title?: (r: T) => ReactNode;
  subtitle?: (r: T) => ReactNode;
  /** Pill di stato, scadenza, rating: sempre in evidenza in alto a destra / sotto il titolo. */
  tags?: (r: T) => ReactNode;
  fields?: (r: T) => [label: string, value: ReactNode][];
  /** Azioni in fondo alla scheda (bottoni): il tap non apre la riga. */
  footer?: (r: T) => ReactNode;
}

export interface ResponsiveListProps<T> {
  columns: ColumnsType<T>;
  dataSource: T[];
  rowKey: string | ((r: T) => Key);
  /** Scheda personalizzata (ha la precedenza su `card`). */
  renderCard?: (row: T) => ReactNode;
  card?: CardSpec<T>;
  onRowClick?: (row: T) => void;
  /** Tap sulla scheda solo su mobile (se la tabella desktop ha già i suoi link). */
  onCardClick?: (row: T) => void;
  loading?: boolean;
  /** false = nessuna paginazione; numero = righe per pagina. */
  pageSize?: number | false;
  /**
   * Scelta delle righe per pagina (AUD-F8.4, 10/50/100): chiave con cui ricordare la scelta nel browser. Con questa chiave
   * `pageSize` è solo il valore iniziale.
   */
  pageSizeKey?: string;
  emptyText?: ReactNode;
  size?: "small" | "middle";
  rowStyle?: (row: T) => React.CSSProperties | undefined;
  /** Altre proprietà passate alla Table su desktop (expandable, rowClassName, ...). */
  tableProps?: Partial<TableProps<T>>;
}

export const monoStyle: React.CSSProperties = { fontFamily: "var(--ap-mono)" };
const clamp2: React.CSSProperties = { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere" };

function DefaultCard<T>({ row, spec }: { row: T; spec: CardSpec<T> }) {
  const fields = spec.fields?.(row) ?? [];
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {spec.code && <div style={{ ...monoStyle, fontSize: 12, color: palette.muted, overflowWrap: "anywhere" }}>{spec.code(row)}</div>}
          {spec.title && <div style={{ fontWeight: 700, color: palette.heading, lineHeight: 1.3, ...clamp2 }}>{spec.title(row)}</div>}
          {spec.subtitle && <div style={{ fontSize: 12.5, color: palette.muted, marginTop: 2, overflowWrap: "anywhere" }}>{spec.subtitle(row)}</div>}
        </div>
      </div>
      {spec.tags && <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 2px", alignItems: "center", marginTop: 8 }}>{spec.tags(row)}</div>}
      {fields.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))", gap: "6px 12px", marginTop: 8 }}>
          {fields.map(([l, v]) => (
            <div key={l} style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: palette.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>{l}</div>
              <div style={{ fontSize: 13, overflowWrap: "anywhere" }}>{v}</div>
            </div>
          ))}
        </div>
      )}
      {spec.footer && (
        <div style={{ marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
          {spec.footer(row)}
        </div>
      )}
    </div>
  );
}

/**
 * Elenco responsivo: su desktop e tablet la Table di antd con le stesse `columns`, sotto 768 px
 * un elenco di schede tappabili (stessa `onRowClick`). Le schede non scorrono mai in orizzontale.
 */
export const PAGE_SIZES = [10, 50, 100] as const;
const storeKey = (k: string) => `ap.pageSize.${k}`;
function readSize(k: string | undefined, fallback: number): number {
  if (!k) return fallback;
  try {
    const v = Number(localStorage.getItem(storeKey(k)));
    return (PAGE_SIZES as readonly number[]).includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

export function ResponsiveList<T extends object>(p: ResponsiveListProps<T>) {
  const mobile = useIsMobile();
  const [page, setPage] = useState(1);
  const [chosen, setChosen] = useState(() => readSize(p.pageSizeKey, p.pageSize === false ? 0 : (p.pageSize ?? 20)));
  const size = p.pageSize === false ? 0 : p.pageSizeKey ? chosen : (p.pageSize ?? 20);
  const sizer = !!p.pageSizeKey && size > 0;
  const changeSize = (n: number) => {
    setChosen(n);
    setPage(1);
    try {
      localStorage.setItem(storeKey(p.pageSizeKey!), String(n));
    } catch {
      /* preferenza non salvata: vale per questa visita */
    }
  };
  const total = p.dataSource.length;
  useEffect(() => {
    if (size && (page - 1) * size >= total) setPage(1);
  }, [total, size, page]);
  const keyOf = (r: T, i: number): Key => (typeof p.rowKey === "function" ? p.rowKey(r) : ((r as Record<string, unknown>)[p.rowKey] as Key) ?? i);

  if (!mobile) {
    return (
      <ResizableTable<T>
        rowKey={p.rowKey as TableProps<T>["rowKey"]}
        size={p.size ?? "middle"}
        loading={p.loading}
        dataSource={p.dataSource}
        columns={p.columns}
        pagination={
          size
            ? sizer
              ? { pageSize: size, current: page, onChange: (pg, ps) => (ps !== size ? changeSize(ps) : setPage(pg)), showSizeChanger: true, pageSizeOptions: PAGE_SIZES.map(String), showTotal: (n, [a, b]) => `${a}-${b} / ${n}` }
              : { pageSize: size, hideOnSinglePage: true, showSizeChanger: false }
            : false
        }
        locale={{ emptyText: p.emptyText }}
        scroll={{ x: "max-content" }}
        onRow={(r) => ({ onClick: p.onRowClick ? () => p.onRowClick!(r) : undefined, style: { ...(p.onRowClick ? { cursor: "pointer" } : {}), ...p.rowStyle?.(r) } })}
        {...p.tableProps}
      />
    );
  }

  const cardClick = p.onCardClick ?? p.onRowClick;
  const rows = size ? p.dataSource.slice((page - 1) * size, page * size) : p.dataSource;
  return (
    <Spin spinning={!!p.loading}>
      {!rows.length && !p.loading ? (
        <div style={{ padding: 16 }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={p.emptyText} />
        </div>
      ) : (
        <div role="list">
          {rows.map((r, i) => (
            <div
              key={keyOf(r, i)}
              role="listitem"
              onClick={cardClick ? () => cardClick(r) : undefined}
              style={{ padding: "12px 14px", borderBottom: `1px solid ${palette.hair}`, cursor: cardClick ? "pointer" : undefined, minWidth: 0, ...p.rowStyle?.(r) }}
            >
              {p.renderCard ? p.renderCard(r) : p.card ? <DefaultCard row={r} spec={p.card} /> : null}
            </div>
          ))}
        </div>
      )}
      {size > 0 && (total > size || (sizer && total > PAGE_SIZES[0])) && (
        <div style={{ padding: 12, display: "flex", justifyContent: "center" }}>
          <Pagination simple={!sizer} size={sizer ? "small" : undefined} current={page} pageSize={size} total={total} showSizeChanger={sizer} pageSizeOptions={PAGE_SIZES.map(String)} onChange={(pg, ps) => (sizer && ps !== size ? changeSize(ps) : setPage(pg))} />
        </div>
      )}
    </Spin>
  );
}
